import { useCallback, useEffect, useMemo } from 'react';
import getConfig from 'next/config';
import { useRouter } from 'next/router';
import { useSearchParams } from 'next/navigation';

import useUser from '~/lib/user';
import useScript from './hook';

const { publicRuntimeConfig } = getConfig();

const { GOOGLE_DATA_CLIENT_ID } = publicRuntimeConfig;

interface PromptType {
  isNotDisplayed: () => boolean;
  isDisplayed: () => boolean;
  isSkippedMoment: () => boolean;
}

declare const window: Window &
  typeof globalThis & {
    google: {
      accounts: {
        id: {
          initialize: (options: { client_id: string }) => void;
          cancel: () => void;
          prompt: (data?: (notification: PromptType) => void) => void;
        };
      };
    };
  };

export default function useOneTap() {
  const { user, isLoading } = useUser();
  const router = useRouter();
  const searchParams = useSearchParams();

  const showOneTap = useCallback(() => {
    if (!window?.google?.accounts?.id) return;

    window.google.accounts.id.prompt((notification: PromptType) => {
      if (notification.isNotDisplayed()) {
        window.google.accounts.id.prompt();
      }
    });
  }, []);

  const hideOneTap = useCallback(() => {
    if (!window?.google?.accounts?.id) return;

    window.google.accounts.id.prompt((notification: PromptType) => {
      if (notification.isDisplayed()) {
        window.google.accounts.id?.cancel();
      }
    });
  }, []);

  const onInit = useCallback(() => {
    if (!window || user?.email || document.getElementById('g_id_onload'))
      return;

    const oneTapEl = document.createElement('div');
    oneTapEl.setAttribute('id', 'g_id_onload');
    oneTapEl.setAttribute(
      'data-login_uri',
      `${process.env.NEXT_PUBLIC_DJANGO_PUBLIC_URL}/api/users/login/google/`,
    );
    oneTapEl.setAttribute('data-itp_support', 'true');
    oneTapEl.setAttribute('data-client_id', GOOGLE_DATA_CLIENT_ID);
    oneTapEl.setAttribute(
      'data-redirect_uri',
      `${router.asPath}?${searchParams.toString()}`,
    );
    oneTapEl.setAttribute('class', 'w-0 h-0 invisible');

    document.body.appendChild(oneTapEl);
  }, [searchParams, router.asPath, user?.email]);

  const onRemove = useCallback(() => {
    hideOneTap();
    document.getElementById('g_id_onload')?.remove();
  }, [hideOneTap]);

  const options = useMemo(
    () => ({
      onInit,
      onRemove,
    }),
    [onRemove, onInit],
  );

  useEffect(() => {
    // If user is logged in, and one tap is showing, hide it (logging in on a different page)
    if (user?.email) {
      onRemove();
    }
  }, [user?.email, onRemove]);

  useScript(
    GOOGLE_DATA_CLIENT_ID && !user?.email && !isLoading
      ? 'https://accounts.google.com/gsi/client'
      : null,
    options,
  );

  return { showOneTap, hideOneTap };
}
